import { useEffect, useState } from 'react';
import { Loader2, Users, X } from 'lucide-react';
import { api } from '../api/client.js';
import { Banner } from './Banner.jsx';

/**
 * Real visitors of a static site (panel table, refreshed hourly from the
 * site's Apache log — counts only). `visits` = the row's summary
 * { today, last7, last_refresh }; null numbers = no day stored yet.
 */
export function VisitsLine({ visits, onClick }) {
  const n = (v) => (v == null ? '—' : v);
  const err = visits?.last_refresh && !visits.last_refresh.ok ? visits.last_refresh : null;
  return (
    <button type="button" onClick={onClick} title={err ? `last count failed: ${err.error} — ${err.detail}` : 'Open 30 days'}
      className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-t2 hover:text-blue hover:underline">
      <Users className="w-3.5 h-3.5 text-ic" />
      Visitors: today {n(visits?.today)} · 7 d {n(visits?.last7)}
      {err && <span className="text-err">!</span>}
    </button>
  );
}

const DAY_LABEL = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

export function VisitsModal({ domain, onClose }) {
  const [state, setState] = useState({ status: 'loading' });
  useEffect(() => {
    let live = true;
    api(`/api/sites/static/${encodeURIComponent(domain)}/visits?days=30`)
      .then((r) => live && setState({ status: 'ok', ...r }))
      .catch((err) => live && setState({ status: 'error', message: `${err.message}${err.body?.detail ? ` — ${err.body.detail}` : ''}` }));
    return () => { live = false; };
  }, [domain]);

  const days = state.days ?? [];
  const max = Math.max(1, ...days.map((d) => d.visitors ?? 0));
  const stored = days.filter((d) => d.visitors != null);
  const sum = stored.reduce((s, d) => s + d.visitors, 0);
  const p = state.period;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-t1/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-bd shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-bd px-5 py-3">
          <Users className="w-4 h-4 text-ic" />
          <h2 className="text-[14px] font-semibold text-t1">Visitors · {domain} · 30 days</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-ctl p-1 text-ic hover:bg-s-3"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 text-[13px] text-t2">
          {state.status === 'loading' && (
            <div className="flex items-center gap-2 py-6 text-t3"><Loader2 className="w-4 h-4 animate-spin" /> Counting from the access log…</div>
          )}
          {state.status === 'error' && <Banner kind="error" title="Couldn't load visitors">{state.message}</Banner>}
          {state.status === 'ok' && (
            <>
              <p className="mb-3 text-xs text-t3">
                {state.rule === 'marker'
                  ? <>A visitor = one browser (same IP + same browser) that loaded <code className="font-mono">{state.marker}</code>, per day. Bots, curl, test browsers and scanner IPs are left out.</>
                  : <>No visit marker set: counts browsers that opened <code className="font-mono">/</code> — <b>less exact</b> (set a marker path the page always loads, on Edit).</>}
              </p>

              <div className="flex h-36 items-end gap-[3px] border-b border-bd" role="img" aria-label="Visitors per day">
                {days.map((d) => (
                  <div key={d.day} className="flex h-full flex-1 flex-col justify-end"
                    title={d.visitors == null ? `${DAY_LABEL(d.day)}: not counted` : `${DAY_LABEL(d.day)}: ${d.visitors} (${d.computer} computer · ${d.phone} phone · ${d.tablet} tablet)`}>
                    {d.visitors == null
                      ? <div className="h-[2px] bg-bd" />
                      : <div className="rounded-t-sm bg-blue" style={{ height: `${Math.max(2, (d.visitors / max) * 100)}%` }} />}
                  </div>
                ))}
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-t3">
                <span>{days[0] && DAY_LABEL(days[0].day)}</span>
                <span>{days.at(-1) && DAY_LABEL(days.at(-1).day)} (UTC days)</span>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <Stat label="Visitor-days (30 d)" value={stored.length ? sum : '—'} sub={`${stored.length} of 30 days counted`} />
                <Stat label="Different visitors" value={p ? p.visitors : '—'}
                  sub={p ? (p.from && p.from > state.from ? `${DAY_LABEL(p.from)} → ${DAY_LABEL(p.to)} — older days are no longer in the log` : 'whole period') : state.period_error?.error} />
                <Stat label="Devices (different visitors)" value={p ? `${p.computer} · ${p.phone} · ${p.tablet}` : '—'} sub="computer · phone · tablet" />
              </div>
              {p && <p className="mt-3 text-xs text-t3">Scanner IPs left out: {p.scanner_ips_dropped}. Days stay in the panel for good; the server's log keeps ~15 days.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-md border border-bd bg-s-2 p-3">
      <div className="text-[11.5px] text-t3">{label}</div>
      <div className="text-[18px] font-semibold text-t1">{value}</div>
      {sub && <div className="text-[11px] text-t3">{sub}</div>}
    </div>
  );
}
